<template>
  <div v-if="!hasDecided" class="cookie-consent" role="dialog" aria-label="Cookie consent" aria-live="polite">
    <div class="cookie-consent__inner">
      <p class="cookie-consent__text">
        We use cookies to keep you signed in and to understand how the site is used.
        See our <RouterLink to="/cookies">Cookie Policy</RouterLink> for details.
      </p>

      <div v-if="!showCustomize" class="cookie-consent__actions">
        <button type="button" class="cookie-consent__btn cookie-consent__btn--ghost" @click="showCustomize = true">
          Customize
        </button>
        <button type="button" class="cookie-consent__btn cookie-consent__btn--outline" @click="handleNecessaryOnly">
          Essential Only
        </button>
        <button type="button" class="cookie-consent__btn cookie-consent__btn--primary" @click="handleAcceptAll">
          Accept All
        </button>
      </div>

      <div v-else class="cookie-consent__customize">
        <label class="cookie-consent__toggle">
          <input type="checkbox" checked disabled />
          <span>
            <strong>Necessary</strong> — required for sign-in and security. Cannot be disabled.
          </span>
        </label>
        <label class="cookie-consent__toggle">
          <input v-model="analyticsChoice" type="checkbox" />
          <span>
            <strong>Analytics</strong> — Google Analytics, helps us understand site usage.
          </span>
        </label>

        <div class="cookie-consent__actions">
          <button type="button" class="cookie-consent__btn cookie-consent__btn--outline" @click="showCustomize = false">
            Back
          </button>
          <button type="button" class="cookie-consent__btn cookie-consent__btn--primary" @click="handleSaveCustom">
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useCookieConsent } from '../../composables/useCookieConsent'

const emit = defineEmits<{ decided: [analytics: boolean] }>()

const { hasDecided, acceptAll, acceptNecessaryOnly, acceptCustom } = useCookieConsent()

const showCustomize = ref(false)
const analyticsChoice = ref(false)

function handleAcceptAll() {
  acceptAll()
  emit('decided', true)
}

function handleNecessaryOnly() {
  acceptNecessaryOnly()
  emit('decided', false)
}

function handleSaveCustom() {
  acceptCustom(analyticsChoice.value)
  emit('decided', analyticsChoice.value)
}

defineOptions({
  name: 'CookieConsentBanner',
})
</script>

<style lang="scss" scoped>
.cookie-consent {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: #fff;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
  padding: 1.25rem;
}

.cookie-consent__inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem;
}

.cookie-consent__text {
  flex: 1 1 320px;
  margin: 0;
  font-size: 0.9rem;
  color: #444;
  line-height: 1.5;

  a {
    color: #333;
    text-decoration: underline;
  }
}

.cookie-consent__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  flex-shrink: 0;
}

.cookie-consent__btn {
  padding: 0.55rem 1.1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;
  white-space: nowrap;

  &--primary {
    background: #333;
    color: #fff;

    &:hover {
      background: #111;
    }
  }

  &--outline {
    background: #fff;
    border-color: #ccc;
    color: #333;

    &:hover {
      border-color: #999;
    }
  }

  &--ghost {
    background: transparent;
    color: #555;

    &:hover {
      color: #111;
      text-decoration: underline;
    }
  }
}

.cookie-consent__customize {
  flex: 1 1 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.cookie-consent__toggle {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.88rem;
  color: #444;
  cursor: pointer;

  input {
    margin-top: 0.2rem;
    flex-shrink: 0;
  }

  input:disabled {
    cursor: not-allowed;
  }
}

@media (max-width: 640px) {
  .cookie-consent__inner {
    flex-direction: column;
    align-items: stretch;
  }

  .cookie-consent__text {
    // flex: 1 1 320px was written for the row layout above, where 320px is a
    // sane min-width. In a column flex container that same basis applies to
    // height instead, forcing this block to try to be 320px tall — reset to
    // the element's natural content size on mobile.
    flex: initial;
  }

  .cookie-consent__actions {
    justify-content: stretch;

    .cookie-consent__btn {
      flex: 1;
    }
  }
}
</style>
